import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import React from "react";
var __jsx = React.createElement;
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { CDN_LINK } from "@globalConfig";
import SavedSearchTagContainer from "@/pages/saved-search/components/SavedSearchTagContainer";
import { useTranslation } from "@i18n";
import SavedSearchCardEdit from './SavedSearchCardEdit';
import { Form } from "@/common/HOCs/rhf/form-provider/FormProvider";
import zod from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { parseJsonSafely } from "@/utils/GlobalJavascriptFunction";
import ConfirmationModal from "@componentsShared/Confirmation/ConfirmationModal";
import { useConfirmation } from "@hooks/useConfirmation";
import { trackEvents } from "@/common/events/events";
import useHelpers from "@hooks/useHelpers";
import { z } from "zod";
import { TAG_TYPES } from "@/pages/saved-search/saved-search";
var RangeValuesSchema = z.object({
  gap: z.number().optional(),
  start: z.number().optional(),
  end: z.number().optional(),
  unit: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  unit_ar: z.string().optional(),
  unit_en: z.string().optional()
});

// Updated to check for the presence of specific fields
var TagSchema = z.union([z.object({
  facetName: z.string(),
  filterField: z.string(),
  displayName: z.string().optional(),
  position: z.number().nullable().optional(),
  range_values: RangeValuesSchema.optional(),
  type: zod.string()
}).refine(function (data) {
  return 'facetName' in data && 'range_values' in data;
}, {
  message: 'This is a range tag, missing required fields.'
}), z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  name_ar: z.string().optional(),
  name_en: z.string().optional(),
  filterField: z.string(),
  parent_ids: z.array(z.string()).optional()
}).refine(function (data) {
  return 'id' in data && 'name' in data;
}, {
  message: 'This is a standard tag, missing required fields.'
})]);
export var SearchEditFormSchema = z.object({
  title: z.string().nonempty({
    message: "Title should not be empty"
  }),
  tags: z.array(TagSchema).min(1, {
    message: "At least one tag is required"
  })
});
var SavedSearchCard = function SavedSearchCard(_ref) {
  var title = _ref.title,
    id = _ref.id,
    _ref$tags = _ref.tags,
    tags = _ref$tags === void 0 ? [] : _ref$tags,
    result_count = _ref.result_count,
    onDeleteSearchCard = _ref.onDeleteSearchCard,
    search_hash = _ref.search_hash,
    onUpdateSearchCard = _ref.onUpdateSearchCard,
    path = _ref.path;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(false),
    isEditingTitle = _useState[0],
    setIsEditingTitle = _useState[1];
  var _useState2 = useState(parseJsonSafely(tags, [])),
    tagList = _useState2[0],
    setTagList = _useState2[1];
  var _useConfirmation = useConfirmation(),
    confirmation = _useConfirmation.confirmation,
    awaitConfirmation = _useConfirmation.awaitConfirmation,
    confirmationHandlers = _useConfirmation.confirmationHandlers;
  var _useConfirmation2 = useConfirmation(),
    deleteConfirmation = _useConfirmation2.confirmation,
    awaitDeleteConfirmation = _useConfirmation2.awaitConfirmation,
    deleteConfirmationHandler = _useConfirmation2.confirmationHandlers;
  var _useHelpers = useHelpers(),
    navigateTo = _useHelpers.navigateTo;
  var defaultValues = useMemo(function () {
    return {
      title: title || "",
      tags: tagList || []
    };
  }, [title, tagList, isEditingTitle]);
  var methods = useForm({
    mode: "all",
    resolver: zodResolver(SearchEditFormSchema),
    defaultValues: defaultValues
  });
  var handleSubmit = methods.handleSubmit,
    reset = methods.reset;
  var onSubmit = handleSubmit(/*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(data) {
      var isConfirmed, updatedData;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.n) {
          case 0:
            _context.n = 1;
            return awaitConfirmation();
          case 1:
            isConfirmed = _context.v;
            if (isConfirmed) {
              updatedData = _objectSpread(_objectSpread({}, data), {}, {
                search_hash: search_hash,
                path: path
              });
              onUpdateSearchCard(updatedData);
              setIsEditingTitle(false);
              reset();
            } else {
              confirmation.setFalse();
              reset();
              setIsEditingTitle(false);
            }
          case 2:
            return _context.a(2);
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }());
  var handleClickDelete = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2() {
      var isConfirmed;
      return _regenerator().w(function (_context2) {
        while (1) switch (_context2.n) {
          case 0:
            _context2.n = 1;
            return awaitDeleteConfirmation();
          case 1:
            isConfirmed = _context2.v;
            if (isConfirmed) {
              onDeleteSearchCard(search_hash);
            }
          case 2:
            return _context2.a(2);
        }
      }, _callee2);
    }));
    return function handleClickDelete() {
      return _ref3.apply(this, arguments);
    };
  }();
  var handleClickSearchResult = function handleClickSearchResult() {
    trackEvents("save_search_results_show", {
      search_title: title,
      filter_count: tagList === null || tagList === void 0 ? void 0 : tagList.length,
      filter_name: tagList === null || tagList === void 0 ? void 0 : tagList.map(function (option) {
        var property = {};
        if (option.type === TAG_TYPES.RANGE) {
          property[option.filterField] = "".concat(option.range_values.from, "-").concat(option.range_values.to);
        } else {
          property[option.filterField] = option.name_en;
        }
        return property;
      })
    });
    navigateTo("/filters?" + path);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "border border-solid border-gray-gainsboro px-3 pt-4 pb-6 bg-white gap-8 content-center rounded-lg",
    role: "listitem"
  }, isEditingTitle ? /*#__PURE__*/React.createElement(Form, {
    methods: methods,
    onSubmit: onSubmit,
    className: "flex gap-2 items-center w-full"
  }, /*#__PURE__*/React.createElement(SavedSearchCardEdit, {
    title: title,
    cancelEdit: function cancelEdit() {
      return setIsEditingTitle(false);
    },
    tagsList: tagList
  })) : /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex justify-between items-center"
  }, /*#__PURE__*/React.createElement("div", {
    className: "line-clamp-1 text-gray-text text-start text-sm font-bold leading-6"
  }, title), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2"
  }, /*#__PURE__*/React.createElement("div", {
    role: "button",
    className: "cursor-pointer",
    onClick: function onClick() {
      return setIsEditingTitle(true);
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: CDN_LINK + "/assets/images/edit-pin.svg",
    alt: "edit-pin",
    className: "cursor-pointer"
  })), /*#__PURE__*/React.createElement("div", {
    role: "button",
    className: "cursor-pointer",
    onClick: function onClick() {
      return handleClickDelete();
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: CDN_LINK + "/assets/images/delete-button.svg",
    alt: "delete",
    role: "button",
    className: "cursor-pointer"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-6"
  }, /*#__PURE__*/React.createElement(SavedSearchTagContainer, {
    tags: tagList,
    title: title
  }), /*#__PURE__*/React.createElement("div", {
    className: "h-px w-full bg-gray-gainsboro"
  }), /*#__PURE__*/React.createElement("button", {
    className: "py-2 px-3 border border-solid border-primary text-primary bg-white rounded w-56 mx-auto text-sm font-bold leading-6 flex gap-0.5 justify-center cursor-pointer hover:bg-white-ghost",
    onClick: function onClick() {
      return handleClickSearchResult();
    }
  }, /*#__PURE__*/React.createElement("span", null, t("view_2", {
    ns: "saved_search"
  })), /*#__PURE__*/React.createElement("span", null, result_count), /*#__PURE__*/React.createElement("span", null, t("search_results", {
    ns: "saved_search"
  }))))), /*#__PURE__*/React.createElement(ConfirmationModal, {
    variant: "default",
    title: t("like_save_changes", {
      ns: "saved_search"
    }),
    isOpen: confirmation.value,
    cancelText: t("cancel", {
      ns: "saved_search"
    }),
    confirmText: t("save_changes", {
      ns: "saved_search"
    }),
    onClose: function onClose() {
      return confirmationHandlers === null || confirmationHandlers === void 0 ? void 0 : confirmationHandlers.handleCancel();
    },
    onConfirm: function onConfirm() {
      return confirmationHandlers === null || confirmationHandlers === void 0 ? void 0 : confirmationHandlers.handleConfirm();
    }
  }), /*#__PURE__*/React.createElement(ConfirmationModal, {
    variant: "danger",
    title: t("like_delete_search", {
      ns: "saved_search"
    }),
    message: t("once_deleted_cannot_recovered", {
      ns: "saved_search"
    }),
    isOpen: deleteConfirmation.value,
    cancelText: t("cancel", {
      ns: "saved_search"
    }),
    confirmText: t("delete_search", {
      ns: "saved_search"
    }),
    onClose: function onClose() {
      return deleteConfirmationHandler === null || deleteConfirmationHandler === void 0 ? void 0 : deleteConfirmationHandler.handleCancel();
    },
    onConfirm: function onConfirm() {
      return deleteConfirmationHandler === null || deleteConfirmationHandler === void 0 ? void 0 : deleteConfirmationHandler.handleConfirm();
    }
  }));
};
export default SavedSearchCard;